import { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { getContent, getHijriToday, getPrayerTimes } from "../lib/api";
import { useLocation } from "../hooks/useLocation";
import { Link } from "react-router-dom";
import {
  Sun, BookOpen, Sparkles, Hash, CalendarDays, Star, Compass,
  HandHeart, Languages, Eye, HelpCircle, Gamepad2, Heart, ChevronRight,
  Moon, Coins, BookOpenCheck, Building2
} from "lucide-react";

const MODULES = (t) => [
  { to: "/prayer",   icon: Sun,         label: t.prayer,   color: "from-emerald-50 to-emerald-100", testid: "module-prayer" },
  { to: "/quran",    icon: BookOpen,    label: t.quran,    color: "from-emerald-50 to-teal-100",   testid: "module-quran" },
  { to: "/names",    icon: Sparkles,    label: t.names,    color: "from-amber-50 to-yellow-100",   testid: "module-names" },
  { to: "/dhikr",    icon: Hash,        label: t.dhikr,    color: "from-teal-50 to-emerald-100",   testid: "module-dhikr" },
  { to: "/hijri",    icon: CalendarDays,label: t.hijri,    color: "from-emerald-50 to-emerald-100",testid: "module-hijri" },
  { to: "/prophets", icon: Star,        label: t.prophets, color: "from-yellow-50 to-amber-100",   testid: "module-prophets" },
  { to: "/qibla",    icon: Compass,     label: t.qibla,    color: "from-teal-50 to-emerald-100",   testid: "module-qibla" },
  { to: "/duas",     icon: HandHeart,   label: t.duas,     color: "from-emerald-50 to-emerald-100",testid: "module-duas" },
  { to: "/arabic",   icon: Languages,   label: t.arabic,   color: "from-amber-50 to-emerald-50",   testid: "module-arabic" },
  { to: "/tafsir",   icon: Eye,         label: t.tafsir,   color: "from-emerald-50 to-teal-100",   testid: "module-tafsir" },
  { to: "/quiz",     icon: HelpCircle,  label: t.quiz,     color: "from-emerald-50 to-emerald-100",testid: "module-quiz" },
  { to: "/game",     icon: Gamepad2,    label: t.game,     color: "from-yellow-50 to-amber-100",   testid: "module-game" },
  { to: "/values",   icon: Heart,       label: t.values,   color: "from-emerald-50 to-emerald-100",testid: "module-values" },
  { to: "/sacred-nights", icon: Moon,           label: t.sacred_nights, color: "from-indigo-50 to-emerald-50",  testid: "module-sacred-nights" },
  { to: "/zakat",         icon: Coins,          label: t.zakat,         color: "from-amber-50 to-yellow-100",   testid: "module-zakat" },
  { to: "/hifz",          icon: BookOpenCheck,  label: t.hifz,          color: "from-emerald-50 to-teal-100",   testid: "module-hifz" },
  { to: "/mosques",       icon: Building2,      label: t.mosques,       color: "from-teal-50 to-emerald-100",   testid: "module-mosques" }
];

function nextPrayerInfo(timings) {
  if (!timings) return null;
  const order = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const list = order.map(name => {
    const [h, m] = (timings[name] || "00:00").split(":").map(Number);
    const d = new Date(today); d.setHours(h, m, 0, 0);
    return { name, date: d };
  });
  let upcoming = list.find(p => p.date > now);
  if (!upcoming) {
    upcoming = { ...list[0], date: new Date(list[0].date.getTime() + 24 * 3600 * 1000) };
  }
  return { upcoming, list };
}

export default function Home() {
  const { t, lang } = useApp();
  const [verse, setVerse] = useState(null);
  const [hadith, setHadith] = useState(null);
  const [hijri, setHijri] = useState(null);
  const [timings, setTimings] = useState(null);
  const [now, setNow] = useState(new Date());
  const { location, error: locError } = useLocation();

  useEffect(() => {
    getContent("verses").then(d => setVerse(d[Math.floor(Math.random() * d.length)]));
    getContent("hadiths").then(d => setHadith(d[Math.floor(Math.random() * d.length)]));
    getHijriToday().then(setHijri).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!location) return;
    getPrayerTimes(location.lat, location.lon)
      .then(d => setTimings(d?.timings))
      .catch(() => {});
  }, [location]);

  const np = nextPrayerInfo(timings);
  let countdown = "--:--:--";
  if (np) {
    const diff = Math.max(0, np.upcoming.date - now);
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    countdown = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }

  const arabicText = verse?.ar;
  const trans = verse?.[lang] || verse?.fr;

  return (
    <div className="space-y-6 fade-in" data-testid="home-page">
      {/* Hero — next prayer */}
      <section className="nour-card p-6 sm:p-8 relative overflow-hidden" data-testid="prayer-countdown-card">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-[#1D9E75]/12 to-[#0F6E56]/10 blur-2xl" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <span className="chip chip-gold" data-testid="home-hijri-chip">
              <CalendarDays className="w-3.5 h-3.5" />
              {hijri ? `${hijri.hijri?.day} ${hijri.hijri?.month?.en} ${hijri.hijri?.year} AH` : t.today}
            </span>
            <span className="text-xs text-nour-text2">{now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-2">
            {t.next_prayer}{np ? ` · ${np.upcoming.name}` : ""}
          </h1>
          <div className="text-5xl sm:text-6xl font-extrabold tracking-tight text-nour-primary tabular-nums" data-testid="prayer-countdown">
            {countdown}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            {location?.label && (
              <Link to="/prayer" className="text-xs text-nour-text2 inline-flex items-center gap-1 hover:text-nour-primary" data-testid="home-location-label">
                📍 {location.label}
              </Link>
            )}
            {locError && (
              <Link to="/prayer" className="text-xs text-amber-700 underline" data-testid="home-location-error">
                {locError} — choisir manuellement
              </Link>
            )}
          </div>
          <div className="mt-5 grid grid-cols-5 gap-2">
            {(np?.list || []).map(p => (
              <div key={p.name} className={`text-center px-2 py-2 rounded-xl ${p.name === np?.upcoming?.name ? "bg-[#E1F5EE] text-nour-primary border border-[#D4A14A]/60 shadow-sm" : "bg-white/60 text-nour-text2 border border-transparent"}`}>
                <div className="text-[10px] sm:text-xs font-semibold">{p.name}</div>
                <div className="text-xs sm:text-sm tabular-nums">{p.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Verse of the day */}
      <section className="nour-card p-6 sm:p-8 geo-watermark" data-testid="verse-of-day">
        <div className="flex items-center justify-between mb-3">
          <span className="chip"><BookOpen className="w-3.5 h-3.5" />{t.verse_of_day}</span>
          <Link to="/quran" className="text-sm text-nour-primary inline-flex items-center gap-1 hover:underline">
            {t.explore} <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        {verse ? (
          <>
            <div className="quran-arabic mb-4 font-arabic-quran" dir="rtl">{arabicText}</div>
            <div className="text-sm text-nour-text2 italic text-center mb-2">{verse.translit}</div>
            <div className="text-base text-nour-text text-center">{trans}</div>
            <div className="text-xs text-nour-text2 mt-3 text-center">{verse.surah} ({verse.ayah})</div>
          </>
        ) : <div className="h-32" />}
      </section>

      {/* Hadith of the day */}
      <section className="nour-card p-6" data-testid="hadith-of-day">
        <div className="flex items-center gap-2 mb-3">
          <span className="chip"><Sparkles className="w-3.5 h-3.5" />{t.hadith_of_day}</span>
        </div>
        {hadith && (
          <>
            <div className="font-arabic-quran text-xl text-right text-nour-primary mb-3" dir="rtl">{hadith.ar}</div>
            <div className="text-base text-nour-text">{`« ${hadith[lang] || hadith.fr} »`}</div>
            <div className="text-xs text-nour-text2 mt-2">— {hadith.source}</div>
          </>
        )}
      </section>

      {/* Module grid */}
      <section data-testid="module-grid">
        <h2 className="font-heading text-xl font-bold mb-4">{t.explore}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {MODULES(t).map(m => {
            const Icon = m.icon;
            return (
              <Link
                key={m.to}
                to={m.to}
                data-testid={m.testid}
                className={`nour-card p-4 flex items-center gap-3 bg-gradient-to-br ${m.color}`}
              >
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm shrink-0">
                  <Icon className="w-5 h-5 text-nour-primary" />
                </div>
                <div className="font-heading font-semibold text-sm text-nour-text">{m.label}</div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
